// Import necessary hooks and libraries
import { useState } from 'react';
import { useAuth } from './useAuth'; // Custom hook for authentication context
import toast from 'react-hot-toast'; // Library for user notifications
import type { Job } from '../types/job'; // Type definition for jobs

/**
 * Custom hook to manage job operations such as updating, toggling status, and deleting jobs.
 */
export function useJobManagement() {
  const [loading, setLoading] = useState(false); // Loading state for async operations
  const { token } = useAuth(); // Retrieve authentication token from the auth context

  /**
   * Updates a job with new data.
   * @param jobId - ID of the job to update.
   * @param updates - Partial job data to update.
   * @returns The updated job data.
   * @throws Error if the job update fails.
   */
  const updateJob = async (jobId: number, updates: Partial<Job>) => {
    if (!token) {
      throw new Error('Authentication required'); // Ensure user is authenticated
    }

    try {
      // Fetch the current job data
      const getResponse = await fetch(`https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/job/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!getResponse.ok) {
        throw new Error('Failed to fetch job data'); // Handle fetch failure
      }

      const currentJob = await getResponse.json(); // Parse current job data

      // Merge current job data with updates
      const updatePayload = {
        job_title: currentJob.job_title,
        business: currentJob.business,
        description: currentJob.description,
        contract: currentJob.contract,
        city: currentJob.city,
        postal_code: currentJob.postal_code,
        salary: currentJob.salary,
        state: currentJob.state,
        ...updates, // Apply updates
      };

      // Send the updated job data to the API
      const response = await fetch(`https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/job/${jobId}`, {
        method: 'PATCH', // Use PATCH for partial updates
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });

      if (!response.ok) {
        throw new Error('Failed to update job'); // Handle update failure
      }

      return await response.json(); // Return updated job data
    } catch (error) {
      console.error('Update job error:', error); // Log error for debugging
      throw error; // Re-throw error for caller
    }
  };

  /**
   * Toggles the active status of a job.
   * @param jobId - ID of the job to update.
   * @param currentStatus - Current active status of the job.
   * @returns The updated job data with the toggled status.
   */
  const toggleJobStatus = async (jobId: number, currentStatus: boolean) => {
    if (!token) {
      throw new Error('Authentication required');
    }

    try {
      setLoading(true); // Set loading to true during the operation
      const job = await updateJob(jobId, { active: !currentStatus }); // Toggle active status
      toast.success(`Offre ${!currentStatus ? 'activée' : 'désactivée'} avec succès`); // Show success message
      return job;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la mise à jour';
      toast.error(message); // Show error message
      throw error;
    } finally {
      setLoading(false); // Set loading back to false
    }
  };

  /**
   * Deletes a job by ID.
   * @param jobId - ID of the job to delete.
   * @throws Error if the deletion fails.
   */
  const deleteJob = async (jobId: number) => {
    if (!token) {
      throw new Error('Authentication required');
    }

    try {
      setLoading(true); // Set loading to true during the operation
      const response = await fetch(`https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/job/${jobId}`, {
        method: 'DELETE', // Use DELETE method
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete job'); // Handle deletion failure
      }

      toast.success('Offre supprimée avec succès'); // Show success message
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erreur lors de la suppression';
      toast.error(message); // Show error message
      throw error;
    } finally {
      setLoading(false); // Set loading back to false
    }
  };

  // Return hook functionality
  return {
    toggleJobStatus, // Function to toggle job status
    deleteJob, // Function to delete a job
    loading, // Loading state
  };
}
